import React, { useState, useEffect, useRef } from 'react';
import { Container, Nav, Navbar, Button } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  FaUser,
  FaPalette,
  FaStore,
  FaExchangeAlt,
  FaChevronDown,
  FaBars,
} from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import Logo from '../../assets/logo7.png';
import '../../styles/nav.scss';
import { toast } from 'react-toastify';

export default function FurnitureNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [currentMode, setCurrentMode] = useState('custom');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const dropdownRef = useRef(null);

  // Load user info
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const role = localStorage.getItem('role');
    const fullName = localStorage.getItem('full_name');
    const savedMode = localStorage.getItem('user_mode') || 'custom';

    if (token && role) {
      setUser({ role, name: fullName });
      setCurrentMode(savedMode);
    }
  }, [location.pathname]);

  // Detect current mode based on URL path
  useEffect(() => {
    if (
      location.pathname.includes('/artisan-dashboard') ||
      location.pathname.includes('/browse-project-requests') ||
      location.pathname.includes('/my-proposals') ||
      location.pathname.includes('/artisan-profile') ||
      location.pathname.includes('/my-projects') ||
      location.pathname.includes('/messaging')
    ) {
      setCurrentMode('custom');
    } else if (
      location.pathname.includes('/artisan-ready-products') ||
      location.pathname.includes('/artisan-product-management') ||
      location.pathname.includes('/artisan-orders') ||
      location.pathname.includes('/artisan-earnings')
    ) {
      setCurrentMode('shop');
    } else if (
      location.pathname.includes('/ready-made-products') ||
      location.pathname.includes('/cart')
    ) {
      setCurrentMode('shop');
    } else if (
      location.pathname.includes('/client-dashboard') ||
      location.pathname.includes('/projects') ||
      location.pathname.includes('/project-updates')
    ) {
      setCurrentMode('custom');
    }
  }, [location.pathname]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Client mode switching
  const handleClientSwitch = () => {
    const newMode = currentMode === 'custom' ? 'shop' : 'custom';
    localStorage.setItem('user_mode', newMode);
    setCurrentMode(newMode);

    if (newMode === 'custom') {
      navigate('/client-dashboard');
      toast.info('Switched to Custom Orders');
    } else {
      navigate('/ready-made-products');
      toast.info('Switched to Ready-Made Shop');
    }
  };

  // Artisan mode switching - FREE SWITCHING, NO LOCKS
  const handleArtisanSwitch = () => {
    const newMode = currentMode === 'custom' ? 'shop' : 'custom';
    localStorage.setItem('user_mode', newMode);
    setCurrentMode(newMode);

    if (newMode === 'custom') {
      navigate('/artisan-dashboard');
      toast.info('Switched to Custom Projects');
    } else {
      navigate('/artisan-ready-products');
      toast.info('Switched to Ready-Made Shop');
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    navigate('/login');
    toast.success('Logout successful');
    setDropdownOpen(false);
  };

  // Show mode switch for logged-in artisans and clients
  const shouldShowModeSwitch =
    user && (user.role === 'client' || user.role === 'artisan');

  return (
    <>
      <Navbar
        expand="lg"
        className={`furniture-navbar ${scrolled ? 'scrolled' : ''}`}
        collapseOnSelect
      >
        <Container>
          {/* Logo */}
          <Navbar.Brand as={Link} to="/" className="d-flex align-items-center">
            <img src={Logo} alt="Artiza logo" className="logo" />
          </Navbar.Brand>

          {/* Mobile Right Section */}
          <div className="mobile-right-section d-flex d-lg-none align-items-center">
            {shouldShowModeSwitch && (
              <Button
                className="mode-switch-btn-mobile"
                onClick={user?.role === 'client' ? handleClientSwitch : handleArtisanSwitch}
                title={`Switch to ${currentMode === 'custom' ? 'Ready-Made Shop' : 'Custom Orders'}`}
              >
                <FaExchangeAlt />
              </Button>
            )}
            <Navbar.Toggle aria-controls="basic-navbar-nav">
              <FaBars />
            </Navbar.Toggle>
          </div>

          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="mx-auto nav-links">
              {!user && (
                <>
                  <Nav.Link as={Link} to="/">
                    Home
                  </Nav.Link>
                  <Nav.Link as={Link} to="/about">
                    About
                  </Nav.Link>
                  <Nav.Link as={Link} to="/contact">
                    Contact
                  </Nav.Link>
                </>
              )}

              {/* Client - Custom Mode */}
              {user?.role === 'client' && currentMode === 'custom' && (
                <>
                  <Nav.Link as={Link} to="/client-dashboard">
                    Dashboard
                  </Nav.Link>
                  <Nav.Link as={Link} to="/projects">
                    My Projects
                  </Nav.Link>
                  <Nav.Link as={Link} to="/project-updates">
                    Project Progress
                  </Nav.Link>
                  <Nav.Link as={Link} to="/messages">
                    Messages
                  </Nav.Link>
                  <Nav.Link as={Link} to="/payments">
                    Payments
                  </Nav.Link>
                  <Nav.Link as={Link} to="/Profile">
                    Profile
                  </Nav.Link>
                </>
              )}

              {/* Client - Shop Mode */}
              {user?.role === 'client' && currentMode === 'shop' && (
                <>
                  <Nav.Link as={Link} to="/ready-made-products">
                    Shop
                  </Nav.Link>
                  <Nav.Link as={Link} to="/cart">
                    Cart
                  </Nav.Link>
                  <Nav.Link as={Link} to="/order-history">
                    Purchases History
                  </Nav.Link>
                  <Nav.Link as={Link} to="/Profile">
                    Profile
                  </Nav.Link>
                </>
              )}

              {/* Artisan - Custom Mode */}
              {user?.role === 'artisan' && currentMode === 'custom' && (
                <>
                  <Nav.Link as={Link} to="/artisan-dashboard">
                    Dashboard
                  </Nav.Link>
                  <Nav.Link as={Link} to="/browse-project-requests">
                    Browse Projects
                  </Nav.Link>
                   <Nav.Link as={Link} to="/my-projects">
                    Ongoing Projects
                  </Nav.Link>
                  <Nav.Link as={Link} to="/my-proposals">
                    Sent Proposals
                  </Nav.Link>
                  <Nav.Link as={Link} to="/messaging">
                    Messages
                  </Nav.Link>
                 
                  <Nav.Link as={Link} to="/artisan-profile">
                    Profile
                  </Nav.Link>
                </>
              )}

              {/* Artisan - Shop Mode */}
              {user?.role === 'artisan' && currentMode === 'shop' && (
                <>
                  <Nav.Link as={Link} to="/artisan-ready-products">
                    My Products
                  </Nav.Link>
                  <Nav.Link as={Link} to="/artisan-product-management">
                    Add Product
                  </Nav.Link>
                  <Nav.Link as={Link} to="/artisan-orders">
                    Orders
                  </Nav.Link>
                  <Nav.Link as={Link} to="/artisan-earnings">
                    Earnings
                  </Nav.Link>
                  <Nav.Link as={Link} to="/artisan-profile">
                    Profile
                  </Nav.Link>
                </>
              )}

              {/* Admin */}
              {user?.role === 'admin' && (
                <>
                  <Nav.Link as={Link} to="/admin-dashboard">
                    Dashboard
                  </Nav.Link>
                  <Nav.Link as={Link} to="/users-management">
                    Users
                  </Nav.Link>
                  <Nav.Link as={Link} to="/projects-management">
                    Projects
                  </Nav.Link>
                  <Nav.Link as={Link} to="/flagged-messages">
                    Flagged
                  </Nav.Link>
                  <Nav.Link as={Link} to="/artisan-applications">
                    Artisan Applications
                  </Nav.Link>
                  <Nav.Link as={Link} to="/financials">
                    Financials
                  </Nav.Link>
                  <Nav.Link as={Link} to="/support">
                    Support
                  </Nav.Link>
                </>
              )}
            </Nav>

            {/* Desktop Actions - Mode Switch visible for logged-in users */}
            <div className="desktop-actions d-none d-lg-flex align-items-center">
              {shouldShowModeSwitch && (
                <Button
                  className="mode-switch-btn"
                  onClick={user?.role === 'client' ? handleClientSwitch : handleArtisanSwitch}
                >
                  <FaExchangeAlt className="me-2" />
                  <span>
                    {currentMode === 'custom' ? (
                      <>
                        Switch to <FaStore /> Shop
                      </>
                    ) : (
                      <>
                        Switch to <FaPalette /> Custom
                      </>
                    )}
                  </span>
                </Button>
              )}

              {!user ? (
                <>
                  <Link to="/login" className="btn-signin">
                    <FaUser /> Log In
                  </Link>
                  <Link to="/register" className="btn-signin">
                    <FaUser /> Sign Up
                  </Link>
                  <Link
                    to="/artisan-join"
                    className="btn-signin artisan-join-btn"
                  >
                    <FaPalette /> Join as an Artisan
                  </Link>
                </>
              ) : (
                <div className="custom-dropdown" ref={dropdownRef}>
                  <button
                    className="custom-dropdown-toggle"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    <span className="user-dropdown">
                      <FaUser className="me-2" />
                      <span className="user-name">
                        {user.name?.split(' ')[0] || 'Account'}
                      </span>
                      <FaChevronDown
                        className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`}
                      />
                    </span>
                  </button>
                  <div
                    className={`custom-dropdown-menu ${dropdownOpen ? 'show' : ''}`}
                  >
                    <button onClick={handleLogout} className="dropdown-item">
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="mobile-actions d-lg-none">
              {!user ? (
                <div className="mobile-auth-buttons">
                  <Link to="/login" className="btn-signin-mobile">
                    <FaUser /> Log In
                  </Link>
                  <Link to="/register" className="btn-signup-mobile">
                    <FaUser /> Sign Up
                  </Link>
                </div>
              ) : (
                <div className="mobile-user-section">
                  <div className="mobile-user-info">
                    <FaUser className="mobile-user-icon" />
                    <span className="mobile-user-name">
                      {user.name || 'User'}
                    </span>
                  </div>
                  <button onClick={handleLogout} className="mobile-logout-btn">
                    Logout
                  </button>
                </div>
              )}
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </>
  );
}